import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SFooterNav, NavItem } from '@/components/ui/organisms/SFooterNav';
import SBack from '@/components/ui/molecules/SBack';
import './MyGame.css';

const STATS = [
  { num: '01', label: 'Assess', desc: 'Read your baseline.' },
  { num: '02', label: 'Plan', desc: 'Avery designs your week.' },
  { num: '03', label: 'Practice', desc: '15·15·30, every visit.' },
  { num: '04', label: 'Improve', desc: 'New numbers, new plan.' },
];

const LoopDiagram = () => (
  <div className="mg-loop-wrap">
    <svg
      className="mg-loop-svg"
      viewBox="0 0 280 280"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <path id="orbitPath" d="M 140 48 A 92 92 0 1 1 139.999 48" fill="none" />

        <radialGradient id="sunGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor="#FFD97A" stopOpacity="0.7" />
          <stop offset="35%"  stopColor="#E8A825" stopOpacity="0.35" />
          <stop offset="70%"  stopColor="#C9A84C" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#C9A84C" stopOpacity="0" />
        </radialGradient>

        <radialGradient id="nodeGrad" cx="40%" cy="35%" r="65%">
          <stop offset="0%"  stopColor="#FFFBF0" />
          <stop offset="100%" stopColor="#F0E5C8" />
        </radialGradient>

        <filter id="dotGlow" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>

      {/* Outer spinning dashed ring */}
      <g className="mg-spin-group">
        <circle cx="140" cy="140" r="110" stroke="#C9A84C" strokeWidth="1" strokeDasharray="3 7" strokeOpacity="0.25" fill="none" />
      </g>

      {/* Orbit track */}
      <circle cx="140" cy="140" r="92" stroke="#C9A84C" strokeWidth="0.75" strokeOpacity="0.12" fill="none" />

      {/* Animated arc arrows */}
      <path d="M 140 48 A 92 92 0 0 1 232 140"  stroke="#C9A84C" strokeWidth="1.5" strokeOpacity="0.6" fill="none" strokeDasharray="4 3" className="mg-flow-arc" style={{ animationDelay: '0s' }} />
      <path d="M 232 140 A 92 92 0 0 1 140 232" stroke="#C9A84C" strokeWidth="1.5" strokeOpacity="0.6" fill="none" strokeDasharray="4 3" className="mg-flow-arc" style={{ animationDelay: '-0.9s' }} />
      <path d="M 140 232 A 92 92 0 0 1 48 140"  stroke="#C9A84C" strokeWidth="1.5" strokeOpacity="0.6" fill="none" strokeDasharray="4 3" className="mg-flow-arc" style={{ animationDelay: '-1.8s' }} />
      <path d="M 48 140 A 92 92 0 0 1 140 48"   stroke="#C9A84C" strokeWidth="1.5" strokeOpacity="0.6" fill="none" strokeDasharray="4 3" className="mg-flow-arc" style={{ animationDelay: '-2.7s' }} />

      {/* Orbiting dot — rendered BEFORE nodes so nodes paint over it */}
      <circle r="10" fill="#C9A84C" fillOpacity="0.2" filter="url(#dotGlow)">
        <animateMotion dur="9s" repeatCount="indefinite" rotate="auto">
          <mpath href="#orbitPath" />
        </animateMotion>
      </circle>
      <circle r="4.5" fill="#C9A84C" filter="url(#dotGlow)">
        <animateMotion dur="9s" repeatCount="indefinite" rotate="auto">
          <mpath href="#orbitPath" />
        </animateMotion>
      </circle>

      {/* Sun glow behind center */}
      <circle className="mg-center-glow" cx="140" cy="140" r="72" fill="url(#sunGlow)" />

      {/* Center circle */}
      <circle cx="140" cy="140" r="40" fill="#FFFCF0" stroke="#E8A825" strokeWidth="1.5" strokeOpacity="0.7" />
      <text x="140" y="135" textAnchor="middle" fill="#9a7a28" fontSize="8"  fontWeight="800" letterSpacing="3" fontFamily="Inter, sans-serif">THE</text>
      <text x="140" y="149" textAnchor="middle" fill="#C9A84C" fontSize="13" fontWeight="800" letterSpacing="3" fontFamily="Inter, sans-serif">LOOP</text>

      {/* Node ping rings */}
      <circle className="mg-node-ping-1" cx="140" cy="48"  r="20" fill="#C9A84C" fillOpacity="0.15" />
      <circle className="mg-node-ping-2" cx="232" cy="140" r="20" fill="#C9A84C" fillOpacity="0.15" />
      <circle className="mg-node-ping-3" cx="140" cy="232" r="20" fill="#C9A84C" fillOpacity="0.15" />
      <circle className="mg-node-ping-4" cx="48"  cy="140" r="20" fill="#C9A84C" fillOpacity="0.15" />

      {/* ASSESS — top */}
      <circle cx="140" cy="48" r="26" fill="url(#nodeGrad)" stroke="#C9A84C" strokeWidth="1.5" strokeOpacity="0.75" />
      <text x="140" y="44" textAnchor="middle" fill="#333" fontSize="7.5" fontWeight="700" letterSpacing="0.5" fontFamily="Inter, sans-serif">ASSESS</text>
      <text x="140" y="55" textAnchor="middle" fill="#C9A84C" fontSize="9" fontWeight="800" fontFamily="Inter, sans-serif">01</text>

      {/* PLAN — right */}
      <circle cx="232" cy="140" r="26" fill="url(#nodeGrad)" stroke="#C9A84C" strokeWidth="1.5" strokeOpacity="0.75" />
      <text x="232" y="136" textAnchor="middle" fill="#333" fontSize="7.5" fontWeight="700" letterSpacing="0.5" fontFamily="Inter, sans-serif">PLAN</text>
      <text x="232" y="147" textAnchor="middle" fill="#C9A84C" fontSize="9" fontWeight="800" fontFamily="Inter, sans-serif">02</text>

      {/* PRACTICE — bottom */}
      <circle cx="140" cy="232" r="26" fill="url(#nodeGrad)" stroke="#C9A84C" strokeWidth="1.5" strokeOpacity="0.75" />
      <text x="140" y="228" textAnchor="middle" fill="#333" fontSize="6.5" fontWeight="700" letterSpacing="0.3" fontFamily="Inter, sans-serif">PRACTICE</text>
      <text x="140" y="239" textAnchor="middle" fill="#C9A84C" fontSize="9" fontWeight="800" fontFamily="Inter, sans-serif">03</text>

      {/* IMPROVE — left */}
      <circle cx="48" cy="140" r="26" fill="url(#nodeGrad)" stroke="#C9A84C" strokeWidth="1.5" strokeOpacity="0.75" />
      <text x="48" y="136" textAnchor="middle" fill="#333" fontSize="6.5" fontWeight="700" letterSpacing="0.3" fontFamily="Inter, sans-serif">IMPROVE</text>
      <text x="48" y="147" textAnchor="middle" fill="#C9A84C" fontSize="9" fontWeight="800" fontFamily="Inter, sans-serif">04</text>
    </svg>
  </div>
);

const MyGame = () => {
  const navigate = useNavigate();
  const [activeNav, setActiveNav] = useState<NavItem>('my-game');

  const handleNavClick = (item: NavItem) => {
    setActiveNav(item);
    switch (item) {
      case 'home':    navigate('/home'); break;
      case 'my-game': navigate('/my-game'); break;
      case 'shop':    navigate('/shop'); break;
      case 'profile': navigate('/profile'); break;
      case 'add':     navigate('/booking-type/location'); break;
      default:        break;
    }
  };

  return (
    <div className="my-game-container">

      {/* Sticky header — same pattern as Shop */}
      <div className="my-game-header">
        <SBack onClick={() => navigate(-1)} />
        <h1 className="my-game-header-title">My Game</h1>
        <span className="my-game-header-spacer" />
      </div>

      <div className="my-game-content">

        {/* Headline */}
        <h1 className="mg-headline">
          The loop that makes{' '}
          <em>better golfers.</em>
        </h1>

        {/* Animated loop diagram */}
        <LoopDiagram />

        {/* Stats grid */}
        <div className="mg-stats-grid">
          {STATS.map((s) => (
            <div key={s.num} className="mg-stat-card">
              <div className="mg-stat-num">{s.num}</div>
              <div className="mg-stat-label">{s.label}</div>
              <div className="mg-stat-desc">{s.desc}</div>
            </div>
          ))}
        </div>

        {/* Body text */}
        <p className="mg-body-text">
          Every Tee Box member runs the same loop. We're building the tools to show you exactly where you are inside it.
        </p>

        {/* CTA */}
        <button className="mg-cta-btn" onClick={() => navigate('/booking-slots?facility=BOX_ASSESSMENT')}>
          Start with the Golf Performance Assessment
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M3 8h10M9 4l4 4-4 4" stroke="#1A1000" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <p className="mg-cta-label">The loop starts at 01</p>

      </div>

      <div className="my-game-footer">
        <SFooterNav activeItem={activeNav} onItemClick={handleNavClick} />
      </div>
    </div>
  );
};

export default MyGame;
